import {
  InternalAdvance,
  InternalAdvanceEdge,
} from '@varo/graphql-schema-types';
import {
  NativeStackHeaderProps,
  Stack,
  useLocalSearchParams,
} from 'expo-router';
import useAccountDetails from 'features/accounts/hooks/useAccountDetails';
import sectionListHelper from 'features/advance/helpers/AdvanceHistory.helpers';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import ExpressiveLgText from 'shared/components/vxl/typography/expressive/ExpressiveLgText';
import BodySmText from 'shared/components/vxl/typography/primary/body/BodySmText';
import TitleLgText from 'shared/components/vxl/typography/primary/title/TitleLgText';
import SectionedContentScreen from 'shared/containers/vxl/SectionedContentScreen/SectionedContentScreen';
import ErrorScreen from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';

const AdvanceHistoryDetails = () => {
  const { id, transactionId } = useLocalSearchParams<{
    id: string;
    transactionId: string;
  }>();
  const { t: translatedText } = useTranslation('advance', {
    keyPrefix: 'advanceDetails',
  });
  const { pastAdvances, loading, errorModel } = useAccountDetails(id);

  const pastAdvancesEdge = pastAdvances?.edges
    ?.map((edge: InternalAdvanceEdge) => edge.node)
    .find((item) => item.id === transactionId);

  const sectionListData = sectionListHelper(
    pastAdvancesEdge as InternalAdvance,
    translatedText,
  );
  const advanceAmount = pastAdvancesEdge
    ? pastAdvancesEdge?.amount?.formatted
    : '';

  return (
    <View style={styles.screen}>
      <Stack.Screen
        options={{
          headerShown: true,
          headerBackVisible: true,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} />
          ),
        }}
      />
      <View style={styles.horizontalPadding}>
        <TitleLgText style={styles.title} numberOfLines={0}>
          {translatedText('title')}
        </TitleLgText>
      </View>

      <View style={styles.headerTitle} testID="amount-tile">
        <BodySmText
          style={styles.amountTitle}
          numberOfLines={0}
          testID="amount-tile-label"
        >
          {translatedText('amountTitle')}
        </BodySmText>
        <ExpressiveLgText numberOfLines={0} testID="amount-tile-value">
          {advanceAmount}
        </ExpressiveLgText>
      </View>
      <View style={styles.divider}>
        <HairLineDivider />
      </View>
      <SectionedContentScreen model={sectionListData} />

      <ScreenTransitionOverlay
        isLoading={loading}
        LoadingComponent={<LoadingSpinner />}
        ErrorComponent={
          errorModel && !loading ? <ErrorScreen {...errorModel} /> : null
        }
      />
    </View>
  );
};

const styles = StyleSheet.create({
  amountTitle: {
    color: theme.color.text.secondary,
    marginBottom: theme.spacing.s,
  },
  divider: {
    marginHorizontal: theme.spacing.m,
  },
  headerTitle: {
    alignItems: 'flex-start',
    columnGap: theme.spacing.m,
    flexDirection: 'column',
    justifyContent: 'flex-start',
    margin: theme.spacing.m,
  },
  horizontalPadding: {
    paddingHorizontal: theme.spacing.m,
  },
  screen: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
    paddingVertical: theme.spacing.m,
  },
  title: {
    marginBottom: theme.spacing.m,
  },
});

export default AdvanceHistoryDetails;
